import { datadogRum } from "@datadog/browser-rum";
import { Dialog as HeadlessUIDialog, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { SUPPORT_FORM_URL } from "@utils/constants";
import { splitMfaAlert } from "@utils/mfaAlert";
import { clsx } from "clsx";
import { Fragment, useEffect, useRef, useState } from "react";

import { Button, Dialog, Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiScraperLoginStatusChoices,
  EhrStartWorkflowAndLoginMutationVariables,
  useEhrStartWorkflowAndLoginMutation,
  useGetEhrLoginStatusQuery,
  useGetHasEhrCredentialsQuery,
} from "@api/graphql/types-and-hooks";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { useOrgSessionAction } from "@hooks/useOrgSessionAction";
import { useAdminData } from "@providers/AdminDataProvider";

import ensoraWordmark from "../../assets/ehr/ensora.svg";
import fronteraIcon from "../../assets/icon.png";

const SERVER_MESSAGE_LOGIN_STATUS = "Login Started";
const INITIAL_LOGIN_ERROR_MESSAGE_MAP = {
  [ApiScraperLoginStatusChoices.FailedCreds]: "",
  [ApiScraperLoginStatusChoices.LoginAttemptsExhausted]: "Too many incorrect attempts. Please try logging in later.",
  [ApiScraperLoginStatusChoices.MfaNotSupported]: "MFA is not supported, please disable it and try again.",
  [ApiScraperLoginStatusChoices.FailedMfa]: "The MFA code couldn’t be verified. Please try again.",
};
const LOGIN_ERROR_MESSAGE_MAP = {
  ...INITIAL_LOGIN_ERROR_MESSAGE_MAP,
  [ApiScraperLoginStatusChoices.FailedCreds]: "Incorrect credentials. Please try again.",
};
// Org-managed (org-level admin) setup never shows the interactive MFA code step —
// an MFA-gated account can't be used for the shared connection. Surface this on
// the form instead of routing to code entry.
const ORG_MFA_UNSUPPORTED_MESSAGE =
  "This EHR account has multi-factor authentication enabled, which isn’t supported for organization-level setup. Please connect an account without MFA.";
// Every MFA-related login outcome. For org-level (org-username-password) setup
// these all mean the same thing — the account is MFA-gated and can't be used —
// so they share one message and never route to the interactive code step.
const MFA_LOGIN_STATUSES: ApiScraperLoginStatusChoices[] = [
  ApiScraperLoginStatusChoices.MfaRequired,
  ApiScraperLoginStatusChoices.FailedMfa,
  ApiScraperLoginStatusChoices.MfaNotSupported,
];

const REACHING_OUT_DURATION_MS = 4500;
const LIMESTONE_50 = "#fcfaf7";

const PULLING_INTERVAL_MILLISECONDS = 1000;
const POLLING_TIMEOUT_MILLISECONDS = 600000; // 10 minutes

enum DialogStep {
  ReachingOut,
  Consent,
  CheckExistingLoginLoading,
  LoginForm,
  NewLoginLoading,
  LoginError,
  MFA,
  LoginTimeout,
}

const AnimatedDots: React.FC = () => (
  <>
    <style>{`
      @keyframes ehrDotPulse {
        0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
        40% { transform: scale(1.2); opacity: 1; }
      }
    `}</style>
    <span className="flex items-center justify-center gap-1.5 mt-3">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="inline-block w-1.5 h-1.5 rounded-full bg-limestone-500"
          style={{
            animation: "ehrDotPulse 1.4s ease-in-out infinite",
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </span>
  </>
);

const StepTransition: React.FC<{ stepKey: string; children: React.ReactNode }> = ({ stepKey, children }) => (
  <>
    <style>{`
      @keyframes ehrFadeInStep {
        from { opacity: 0; transform: translateY(4px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    <div key={stepKey} style={{ animation: "ehrFadeInStep 600ms ease-in-out forwards" }}>
      {children}
    </div>
  </>
);

interface EHRLoginDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setSuccessLogin: (open: boolean) => void;
  setLoginOutcome?: (outcome: "existing" | "fresh") => void;
  onLoginStarted?: () => void;
  // Fires once an MFA code has been relayed. The dialog then closes without
  // reporting success and leaves the rest of the login to the connection
  // badge, and setOpen(false) also means Cancel, so a caller that must act
  // on this login (e.g. start an import) hooks in here instead.
  onMfaCodeSubmitted?: () => void;
  showIntroFlow?: boolean;
}

export const EHRLoginDialog: React.FC<EHRLoginDialogProps> = ({
  open,
  setOpen,
  setSuccessLogin,
  setLoginOutcome,
  onLoginStarted,
  onMfaCodeSubmitted,
  showIntroFlow,
}) => {
  const [step, setStep] = useState<DialogStep>(DialogStep.CheckExistingLoginLoading);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(false);
  const [isWaitingForSync, setIsWaitingForSync] = useState<boolean>(false);
  const [loginWithSavedCredentials, setLoginWithSavedCredentials] = useState<boolean>(false);
  // True briefly after an MFA code is submitted: keeps the button spinning for a
  // moment before the dialog closes (progress then shows on the connection badge).
  const [isSubmittingMfa, setIsSubmittingMfa] = useState<boolean>(false);
  const [credentialError, setCredentialError] = useState(false);
  const [usernameReadOnly, setUsernameReadOnly] = useState(true);
  const [passwordReadOnly, setPasswordReadOnly] = useState(true);
  const reachingOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the post-MFA-submit "close after 2s" timer so it can be cancelled if
  // the dialog reopens (e.g. auto-reopen for a retry) before it fires.
  const mfaCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Dedupes "EHR connected" so a shared getEhrLoginStatusQuery cache entry
  // doesn't fire it more than once. `step` never moves off NewLoginLoading/MFA
  // on success (the dialog closes externally instead), and the same query key
  // is still being polled by EHRConnectionProvider (e.g. while
  // isConnectionActive stays true post-login) — a later refetch there updates
  // this cache entry too, re-running the success effects with a fresh
  // `ehrLoginStatus` reference while step is unchanged.
  const loginSuccessCapturedRef = useRef(false);
  // Dedupes "ehr_login_modal_opened" per open cycle — step can cycle back to
  // LoginForm within the same open (e.g. after a failed attempt), which must
  // not be recounted as a fresh modal open.
  const loginModalOpenedCapturedRef = useRef(false);

  const queryClient = useQueryClient();
  const {
    externalOrganizationName,
    organizationLogo,
    isRethinkUser,
    isCentralReachUser,
    isDataWarehouseOrg,
    isOrgUsernamePasswordOrg,
    isEnsoraUser,
  } = useAdminData();
  const isCRDataWarehouse = isCentralReachUser && isDataWarehouseOrg;
  // Derived locally rather than read from EHRConnectionProvider: that
  // provider imports this dialog, so consuming its context here would
  // close an import cycle. Same predicate as the exported usesOrgSession.
  const usesOrgSession = isCentralReachUser && isOrgUsernamePasswordOrg;
  // Both modes store credentials at the org level — there is no per-provider
  // login form, so the dialog must not attempt a per-provider credential check.
  const isOrgManagedEhr = isCRDataWarehouse || isOrgUsernamePasswordOrg;
  // INT-36: CentralReach on shared org credentials connects by queueing a
  // LOGIN action on the org-wide session. The action saves the credentials
  // and verifies them in one call, and its outcome — not the connection
  // badge — is what the dialog waits on: while the login is in flight the
  // org credential reads VERIFYING, which ehrConnection surfaces as
  // DISCONNECTED, and the dialog would read that as a failure.
  const { captureEhrConnected, captureEhrLoginFailed, captureEhrLoginModalOpened, captureEhrLoginSubmitted } =
    useEhrAnalytics();

  // ─── Queries ───────────────────────────────────────────────

  const { data: hasEhrCredentialsData, isError: hasEhrCredentialsError } = useGetHasEhrCredentialsQuery(
    {},
    {
      queryKey: ["getHasEhrCredentials"],
      gcTime: 0,
      staleTime: 0,
      // Org-managed orgs use org-level credentials — no per-provider login form.
      enabled: open && step === DialogStep.CheckExistingLoginLoading && !isOrgManagedEhr,
    },
  );

  const hasEhrCredentialsResolved = isOrgManagedEhr || hasEhrCredentialsData !== undefined || hasEhrCredentialsError;
  const hasCredentials = !isOrgManagedEhr && hasEhrCredentialsData?.hasEhrCredentials?.hasCredentials === true;

  const { data: initialEhrLoginStatus, isError: loginError } = useGetEhrLoginStatusQuery(
    {},
    {
      queryKey: ["initialgetEHRLoginStatusQuery"],
      gcTime: 0,
      staleTime: 0,
      enabled: open && step === DialogStep.CheckExistingLoginLoading && hasEhrCredentialsResolved,
    },
  );
  const { data: ehrLoginStatus } = useGetEhrLoginStatusQuery(
    {},
    {
      queryKey: ["getEhrLoginStatusQuery"],
      gcTime: 0,
      staleTime: 0,
      // Never for an org-session login. getLoginStatus reads a per-provider
      // ScraperLogin row, which the org session does not create — so the
      // status comes back empty, the status switch hits its `default`, and
      // loginFailed() lands the dialog on LoginError with a null message
      // (the generic "you've been signed out" copy) even though the LOGIN
      // request was queued successfully. queueAndWait is the authority on
      // that path.
      enabled: open && pollingEnabled && !usesOrgSession,
      refetchInterval: PULLING_INTERVAL_MILLISECONDS,
    },
  );
  const ehrStartWorkflowAndLoginMutation = useEhrStartWorkflowAndLoginMutation();
  const { queue, queueAndWait } = useOrgSessionAction();

  const loginStatus = ehrLoginStatus?.getLoginStatus?.getLoginStatus?.status;

  // ─── Handlers ──────────────────────────────────────────────

  // The poll reads the cache entry EHRConnectionProvider also uses, which still
  // holds the status from before this login: on a provider's first login, an
  // empty one, which the status effect below reads as a failure. Resetting the
  // entry empties it, so the dialog waits for a fresh answer about this login,
  // and refetches it for the provider too.
  const pollNewLogin = () => {
    // Not awaited: resetQueries swallows refetch errors rather than rejecting,
    // and the dialog's own poll asks again a second later.
    void queryClient.resetQueries({ queryKey: ["getEhrLoginStatusQuery"] });
    setPollingEnabled(true);
  };

  // `captureReason` lets a caller that knows loginFailed() was triggered by a
  // real failure (e.g. the MFA code relay itself erroring) force the analytics
  // capture regardless of the currently-polled loginStatus. Without it, the
  // MfaRequired check below only reflects *why the poll last updated*, not *why
  // loginFailed() was invoked* — a code-relay failure during the MFA step runs
  // while loginStatus is still (stale) MfaRequired from the prior poll, and
  // would otherwise be silently dropped from the connect funnel.
  const loginFailed = (captureReason?: ApiScraperLoginStatusChoices | "mfa_relay_error") => {
    setPollingEnabled(false);
    setLoginWithSavedCredentials(false);
    setIsSubmittingMfa(false);

    // Inline setErrorMessage below renders the mapped friendly copy from
    // LOGIN_ERROR_MESSAGE_MAP — no toast needed (the raw `alert` field from
    // getLoginStatus was leaking scraper internals).

    // Org-level (org-username-password) setup never uses the interactive MFA code
    // step: any MFA outcome (required / failed / not-supported) means the account
    // is MFA-gated and can't back the shared connection — route to the form with
    // one consistent message (matches OrgAdminConnectBadge). CR data-warehouse is
    // intentionally excluded; it keeps the normal interactive-MFA flow.
    if (isOrgUsernamePasswordOrg && loginStatus && MFA_LOGIN_STATUSES.includes(loginStatus)) {
      captureEhrLoginFailed(captureReason ?? loginStatus);
      setStep(DialogStep.LoginForm);
      setErrorMessage(ORG_MFA_UNSUPPORTED_MESSAGE);
      return;
    }

    // MfaRequired here (non-org-managed) routes to the interactive code-entry step
    // below — that's normal mid-flow progression, not a failure, so it must not
    // fire captureEhrLoginFailed (it would corrupt the connect funnel with a false
    // failure before the user even enters a code) UNLESS the caller passed an
    // explicit captureReason, meaning this call is a genuine failure that merely
    // happens to run while loginStatus is still (stale) MfaRequired.
    if (captureReason || loginStatus !== ApiScraperLoginStatusChoices.MfaRequired) {
      captureEhrLoginFailed(captureReason ?? loginStatus);
    }

    switch (loginStatus) {
      case ApiScraperLoginStatusChoices.MfaRequired:
        setErrorMessage(null);
        setStep(DialogStep.MFA);
        // Keep polling so a later terminal status routes away from the code step.
        setPollingEnabled(true);
        return;
      // FailedMfa: send the user back to a clean login form — they already saw
      // the failure in the strip/popover, so no additional error copy needed.
      case ApiScraperLoginStatusChoices.FailedMfa:
        setStep(DialogStep.LoginForm);
        setErrorMessage(null);
        return;
      // MfaNotSupported: keep the status-specific copy so the user knows to disable MFA.
      case ApiScraperLoginStatusChoices.MfaNotSupported:
        setStep(DialogStep.LoginForm);
        setErrorMessage(LOGIN_ERROR_MESSAGE_MAP[ApiScraperLoginStatusChoices.MfaNotSupported]);
        return;
      case ApiScraperLoginStatusChoices.FailedCreds: {
        setStep(DialogStep.LoginForm);
        // Use EHR-specific alert (e.g. Motivity multi-org / no-org) when available;
        // otherwise fall back to the generic wrong-credentials copy.
        const sessionAlert = ehrLoginStatus?.getLoginStatus?.getLoginStatus?.alert;
        setErrorMessage(sessionAlert ?? LOGIN_ERROR_MESSAGE_MAP[loginStatus]);
        setCredentialError(!sessionAlert);
        return;
      }
      case ApiScraperLoginStatusChoices.LoginAttemptsExhausted:
        if (ehrLoginStatus?.getLoginStatus?.getLoginStatus?.loginAttemptsExhausted) {
          setErrorMessage(LOGIN_ERROR_MESSAGE_MAP[loginStatus]);
        }
        setCredentialError(true);
        setStep(DialogStep.LoginForm);
        return;
      default:
        datadogRum.addError("Unexpected login status", { loginStatus });
        setStep(DialogStep.LoginError);
        setErrorMessage(null);
        return;
    }
  };

  const handleLoginSuccess = (outcome: "existing" | "fresh") => {
    setLoginOutcome?.(outcome);
    setSuccessLogin(true);
    if (loginSuccessCapturedRef.current) return;
    loginSuccessCapturedRef.current = true;
    captureEhrConnected(outcome);
  };

  const startLoginWithSavedCredentials = () => {
    setLoginWithSavedCredentials(true);
    ehrStartWorkflowAndLoginMutation.mutate(
      { username: null, password: null },
      {
        onSuccess: (data) => {
          if (data.ehrStartWorkflowAndLogin?.message === SERVER_MESSAGE_LOGIN_STATUS) {
            if (onLoginStarted) {
              onLoginStarted();
              return;
            }
            setStep(DialogStep.NewLoginLoading);
            pollNewLogin();
          } else {
            loginFailed();
          }
        },
        onError: () => {
          // Not routed through loginFailed(): loginStatus is unset this early
          // (polling hasn't started), so loginFailed()'s switch would hit the
          // `default` case and reroute to the LoginError screen instead of the
          // plain login form this path has always shown on a relay error. Still
          // capture the failure directly so it isn't dropped from the funnel.
          captureEhrLoginFailed("saved_login_relay_error");
          setPollingEnabled(false);
          setLoginWithSavedCredentials(false);
          setStep(DialogStep.LoginForm);
          setErrorMessage(null);
        },
      },
    );
  };

  const closeDialog = () => {
    setOpen(false);
    setPollingEnabled(false);
    setSuccessLogin(false);
  };

  // ─── Effects ───────────────────────────────────────────────

  useEffect(() => {
    if (step !== DialogStep.CheckExistingLoginLoading) return;

    if (loginError && !initialEhrLoginStatus) {
      setErrorMessage("Unable to check login status. Please try again or contact support.");
      setStep(DialogStep.LoginError);
      setPollingEnabled(false);
      return;
    }

    if (!initialEhrLoginStatus) return;

    const topLevel = initialEhrLoginStatus.getLoginStatus;
    const topStatus = !!topLevel?.status;
    const session = topLevel?.getLoginStatus;

    const goToLoginFormOrSavedLogin = () => {
      if (hasCredentials) {
        startLoginWithSavedCredentials();
      } else {
        setErrorMessage("");
        setStep(DialogStep.LoginForm);
        setPollingEnabled(false);
      }
    };

    if (loginError || !topStatus) {
      goToLoginFormOrSavedLogin();
      return;
    }

    if (!session) {
      goToLoginFormOrSavedLogin();
      return;
    }

    const status = session.status;

    // Org-level (org-username-password) setup manages the shared credentials —
    // there is no per-provider "existing session" to reuse, so always land on the
    // credential form regardless of the polled status. A stale Success must not
    // auto-complete/close the dialog (skipping the form), and MFA states surface
    // the MFA-unsupported note rather than the interactive code step. CR
    // data-warehouse is excluded and keeps the normal flow below.
    if (isOrgUsernamePasswordOrg) {
      setErrorMessage(MFA_LOGIN_STATUSES.includes(status) ? ORG_MFA_UNSUPPORTED_MESSAGE : "");
      setStep(DialogStep.LoginForm);
      setPollingEnabled(false);
      return;
    }

    if (status === ApiScraperLoginStatusChoices.Success) {
      handleLoginSuccess("existing");
      setPollingEnabled(false);
      setIsWaitingForSync(true);
      return;
    }
    if (status === ApiScraperLoginStatusChoices.MfaRequired) {
      setErrorMessage("");
      setStep(DialogStep.MFA);
      // Keep polling on the MFA step so a terminal poll result (FAILED_MFA after
      // the attempts are exhausted, or SUCCESS) routes the open dialog away from
      // the code step rather than leaving it stuck (PROA-1534).
      setPollingEnabled(true);
      return;
    }
    if (status === ApiScraperLoginStatusChoices.FailedMfa) {
      setErrorMessage(INITIAL_LOGIN_ERROR_MESSAGE_MAP[status]);
      setStep(DialogStep.LoginForm);
      setPollingEnabled(false);
      return;
    }
    if (status === ApiScraperLoginStatusChoices.FailedCreds || status == ApiScraperLoginStatusChoices.MfaNotSupported) {
      const sessionAlert = status === ApiScraperLoginStatusChoices.FailedCreds ? session.alert : null;
      setErrorMessage(sessionAlert ?? INITIAL_LOGIN_ERROR_MESSAGE_MAP[status]);
      setStep(DialogStep.LoginForm);
      setPollingEnabled(false);
      return;
    }
    if (status === ApiScraperLoginStatusChoices.LoginAttemptsExhausted) {
      if (session.loginAttemptsExhausted) {
        setErrorMessage(INITIAL_LOGIN_ERROR_MESSAGE_MAP[status]);
      } else {
        setErrorMessage(null);
      }
      goToLoginFormOrSavedLogin();
      return;
    }
    if (status === ApiScraperLoginStatusChoices.LoggingIn) {
      setStep(DialogStep.NewLoginLoading);
      setPollingEnabled(true);
      return;
    }

    goToLoginFormOrSavedLogin();
  }, [initialEhrLoginStatus, loginError, step, hasCredentials]);

  useEffect(() => {
    if (!ehrLoginStatus) return;
    const status = ehrLoginStatus?.getLoginStatus?.getLoginStatus?.status;

    if (step === DialogStep.NewLoginLoading) {
      if (status === ApiScraperLoginStatusChoices.Success) {
        handleLoginSuccess("fresh");
        setPollingEnabled(false);
        setIsWaitingForSync(true);
        return;
      }

      if (status !== ApiScraperLoginStatusChoices.LoggingIn || !ehrLoginStatus.getLoginStatus?.status) {
        loginFailed();
      }
      return;
    }

    // On the MFA step, react to a terminal poll outcome so the open code dialog
    // doesn't get stuck while the badge already shows the result (PROA-1534).
    // While the status is still MFA_REQUIRED (waiting / validating a submitted
    // code) we stay put so the submit spinner and re-prompt aren't disturbed.
    if (step === DialogStep.MFA) {
      if (status === ApiScraperLoginStatusChoices.Success) {
        handleLoginSuccess("existing");
        setPollingEnabled(false);
        setIsWaitingForSync(true);
        return;
      }
      if (
        status &&
        status !== ApiScraperLoginStatusChoices.MfaRequired &&
        status !== ApiScraperLoginStatusChoices.LoggingIn
      ) {
        // Terminal (FAILED_MFA after attempts exhausted, failed creds, etc.):
        // loginFailed() routes to the login form with the mapped terminal copy.
        loginFailed();
      }
    }
  }, [ehrLoginStatus, step]);

  useEffect(() => {
    // The MFA step polls to observe the eventual outcome, but the scraper owns
    // its own 2-minute MFA deadline, so the generic login-timeout must not apply
    // there (it would wrongly flip the open code dialog to LoginTimeout).
    if (!pollingEnabled || step === DialogStep.MFA) return;
    const timerId = setTimeout(() => {
      setPollingEnabled(false);
      setStep(DialogStep.LoginTimeout);
    }, POLLING_TIMEOUT_MILLISECONDS);
    return () => clearTimeout(timerId);
  }, [pollingEnabled, step]);

  // Auto-advance from ReachingOut to Consent
  useEffect(() => {
    if (step === DialogStep.ReachingOut) {
      reachingOutTimerRef.current = setTimeout(() => {
        setStep(DialogStep.Consent);
      }, REACHING_OUT_DURATION_MS);
      return () => {
        if (reachingOutTimerRef.current) clearTimeout(reachingOutTimerRef.current);
      };
    }
  }, [step]);

  // ─── Form ─────────────────────────────────────────────────

  /** Submit org credentials through the org-wide session (INT-36).
   *
   * Extracted from the form's mutationFn: that handler already carries the
   * per-provider, MFA-relay and soft-block branches, and folding this in
   * made it the most complex function in the file.
   */
  const submitOrgSessionLogin = async (params: { username?: string | null; password?: string | null }) => {
    // Belt and braces: several effects flip pollingEnabled on, and none of
    // them apply here.
    setPollingEnabled(false);
    setStep(DialogStep.NewLoginLoading);
    const credentials = {
      actionType: "LOGIN" as const,
      username: params.username,
      password: params.password,
    };
    try {
      // Legacy parity: where the per-provider path handed off on "Login
      // Started", hand off here too — close the dialog, let the badge and
      // the "Connecting to your EHR..." toast carry the rest. A
      // CentralReach login pays a captcha solve, so holding the dialog open
      // for it would park the admin on a spinner for minutes. Awaited (not
      // fire-and-forget) so a rejected request — not an admin, disconnect
      // required, another admin already connecting — still reports inline.
      if (onLoginStarted) {
        const { requestId, message } = await queue(credentials);
        if (!requestId) {
          // Resolved without a row: nothing is queued, so handing off would
          // close the dialog and leave the badge reporting progress on work
          // that does not exist. Keep the admin here with the reason.
          setStep(DialogStep.LoginForm);
          setErrorMessage(message ?? "The EHR request could not be queued.");
          return;
        }
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
        onLoginStarted();
        return;
      }

      // No onLoginStarted: the onboarding flow, which opens this dialog
      // with handleErrorsInline so every failure renders in the form. The
      // dialog is then the only place an outcome can land, so wait it out.
      //
      // Note the header badge does *not* land here — it passes
      // fromHeaderBadge, not handleErrorsInline, so it takes the hand-off
      // above. That matches the per-provider flow it replaces, and a
      // rejected credential still reaches the admin through the badge
      // (CREDENTIALS_FAILED -> OrgAdminConnectBadge) rather than inline.
      const outcome = await queueAndWait(credentials);
      if (outcome.status === "CANCELLED") return;
      if (outcome.status === "SUCCEEDED") {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
        queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
        // Not onLoginStarted(): queueAndWait already resolved, so the login
        // is *done*, not starting.
        handleLoginSuccess("fresh");
        return;
      }
      // The request row carries the real reason: rejected credentials, an
      // MFA-gated account, or an unreachable EHR.
      setStep(DialogStep.LoginForm);
      setErrorMessage(outcome.error ?? "We couldn't connect to the EHR. Please try again.");
    } catch (error) {
      // Rejected before queueing — not an admin, the connection needs
      // disconnecting first, or another admin is already connecting. These
      // messages are written for the user, so show them as-is.
      setStep(DialogStep.LoginForm);
      setErrorMessage(error instanceof Error ? error.message : "Unable to start verification.");
    }
  };

  const { formState, onSubmit, reset, RegisteredFormInput, getValues, trigger } =
    useFormUtils<EhrStartWorkflowAndLoginMutationVariables>({
      mutationFn: async (params) => {
        captureEhrLoginSubmitted();
        setCredentialError(false);

        if (usesOrgSession) {
          await submitOrgSessionLogin(params);
          return;
        }

        await ehrStartWorkflowAndLoginMutation.mutateAsync(params, {
          onSuccess: async (data) => {
            // Soft block (status === false), e.g. another admin is already
            // verifying the shared org credentials. Not a login failure — keep
            // the dialog open on the form, show the message inline, and refresh
            // the connection status so it reflects the in-flight verification.
            if (data.ehrStartWorkflowAndLogin?.status === false) {
              setPollingEnabled(false);
              setStep(DialogStep.LoginForm);
              setErrorMessage(data.ehrStartWorkflowAndLogin.message ?? "Unable to start verification.");
              queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
              queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
              return;
            }
            if (data.ehrStartWorkflowAndLogin?.message === SERVER_MESSAGE_LOGIN_STATUS) {
              if (onLoginStarted) {
                onLoginStarted();
                return;
              }
              setStep(DialogStep.NewLoginLoading);
              pollNewLogin();
              return;
            }
            loginFailed();
          },
          onError: async () => {
            setPollingEnabled(false);
            loginFailed();
          },
        });
      },
    });

  // MFA submit: send ONLY the code. The dialog's shared form has username/
  // password registered as required (from the login-form step), but on the MFA
  // step — especially the auto-opened / saved-credential path — those are empty
  // and not needed (the scraper relays the code to its live session). So we
  // validate just the `mfa` field and post it without credentials, instead of
  // the full-form onSubmit (which would fail with "Email/Password is required").
  const submitMfaCode = async () => {
    // Guard against a double-submit (e.g. a second Enter press while the first
    // relay is still in flight). The primary button is disabled via
    // isLoginInFlight, but the MFA field's Enter handler also calls this.
    if (isSubmittingMfa) return;
    const isMfaValid = await trigger("mfa");
    if (!isMfaValid) return;
    setCredentialError(false);
    setErrorMessage(null);
    setIsSubmittingMfa(true);
    await ehrStartWorkflowAndLoginMutation.mutateAsync(
      { username: null, password: null, mfa: getValues("mfa") },
      {
        // No status===false (soft-block) handling here: the soft block only
        // applies to org-managed setup, which never reaches the interactive MFA
        // code step, so a code relay can't collide with a concurrent admin.
        onSuccess: (data) => {
          if (data.ehrStartWorkflowAndLogin?.message === SERVER_MESSAGE_LOGIN_STATUS) {
            // Hold the spinning button briefly so the submit registers, then
            // close and let the connection badge reflect progress — consuming +
            // validating the code, product selection and loading the home page
            // can take a while. If the code is wrong the scraper sets
            // MFA_REQUIRED again and the provider auto-reopens this dialog.
            // Store the timer so a reopen before it fires can cancel it (avoids
            // a stale callback closing a dialog the user is actively using).
            onMfaCodeSubmitted?.();
            if (mfaCloseTimerRef.current) clearTimeout(mfaCloseTimerRef.current);
            mfaCloseTimerRef.current = setTimeout(() => setOpen(false), 2000);
          } else {
            setIsSubmittingMfa(false);
            // The relay call succeeded but returned an unexpected message — a
            // genuine failure, not the normal "still MFA_REQUIRED" mid-flow
            // state, so force the capture.
            loginFailed("mfa_relay_error");
          }
        },
        onError: () => {
          setIsSubmittingMfa(false);
          setPollingEnabled(false);
          // The relay call itself errored (network/GraphQL) — force the capture
          // for the same reason as above.
          loginFailed("mfa_relay_error");
        },
      },
    );
  };

  // Cancel a pending post-submit close timer on unmount so it can't fire late.
  useEffect(
    () => () => {
      if (mfaCloseTimerRef.current) clearTimeout(mfaCloseTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (open) {
      // A fresh open (incl. auto-reopen for a retry) cancels any pending
      // close-after-submit timer so it can't close the reopened dialog.
      if (mfaCloseTimerRef.current) {
        clearTimeout(mfaCloseTimerRef.current);
        mfaCloseTimerRef.current = null;
      }
      reset();
      setErrorMessage(null);
      setCredentialError(false);
      loginSuccessCapturedRef.current = false;
      loginModalOpenedCapturedRef.current = false;
      if (showIntroFlow) {
        setStep(DialogStep.ReachingOut);
      } else if (onLoginStarted) {
        setStep(DialogStep.LoginForm);
      } else {
        setStep(DialogStep.CheckExistingLoginLoading);
      }
      setPollingEnabled(false);
      setIsWaitingForSync(false);
      setLoginWithSavedCredentials(false);
      setIsSubmittingMfa(false);
      setUsernameReadOnly(true);
      setPasswordReadOnly(true);
    } else {
      setPollingEnabled(false);
      setIsWaitingForSync(false);
    }
  }, [open]);

  // Fires once per open cycle, the first time the email/password fields
  // actually render — not on CheckExistingLoginLoading/MFA/intro steps, and not
  // again if a failed attempt cycles step back to LoginForm within the same open.
  useEffect(() => {
    if (open && step === DialogStep.LoginForm && !loginModalOpenedCapturedRef.current) {
      loginModalOpenedCapturedRef.current = true;
      captureEhrLoginModalOpened();
    }
  }, [open, step, captureEhrLoginModalOpened]);

  // ─── Derived state ────────────────────────────────────────

  const loginAttemptsExhausted =
    ehrLoginStatus?.getLoginStatus?.getLoginStatus?.loginAttemptsExhausted ??
    initialEhrLoginStatus?.getLoginStatus?.getLoginStatus?.loginAttemptsExhausted ??
    false;

  const isIntroStep = step === DialogStep.ReachingOut || step === DialogStep.Consent;
  // On the MFA step we keep polling to observe the outcome, but that must NOT
  // mark the form in-flight (it would disable the "Continue" submit button and
  // show a permanent spinner) — only an actual code submit (isSubmittingMfa)
  // counts as in-flight there.
  const isLoginInFlight =
    (pollingEnabled && step !== DialogStep.MFA) ||
    ehrStartWorkflowAndLoginMutation.isPending ||
    isWaitingForSync ||
    isSubmittingMfa;
  // Auto-login with saved credentials reuses the centered "Reaching out" card
  // (logo + animated dots, no CTAs) — only the copy changes to "Logging in".
  const isSavedCredentialLoading = step === DialogStep.NewLoginLoading && loginWithSavedCredentials;
  const isReachingOutCard = step === DialogStep.ReachingOut || isSavedCredentialLoading;
  const isLimestoneBackdrop = showIntroFlow && !isReachingOutCard;
  const errorInputClassName = credentialError ? "ring-red-500 ring-2" : "";

  // ─── Shared login step content ────────────────────────────

  // prettier-ignore
  const renderLoginStepContent = () => ( // NOSONAR
    <>
      <div className="flex flex-col items-center mt-3 mb-8">
        {isEnsoraUser ? (
          <img className="w-[160px] h-auto" src={ensoraWordmark} alt="Ensora" />
        ) : (
          <img className="w-20 h-20" src={organizationLogo} alt="logo" />
        )}
      </div>
      {step === DialogStep.CheckExistingLoginLoading ? (
        <div className="flex flex-col items-center gap-4 py-8">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-limestone-400" />
        </div>
      ) : step === DialogStep.LoginError ? ( // NOSONAR
        <div className="flex flex-col items-start gap-2 py-8 p-2">
          <Label size="large" displayType="loud" colorType="primary">
            Sorry, something went wrong.
          </Label>
          {errorMessage ? (
            <Label size="medium" colorType="primary">
              {errorMessage}
            </Label>
          ) : (
            <Label size="medium" colorType="primary">
              We've encountered an issue. You've been signed out, please log in again to restart the process. If the
              issue continues,{" "}
              <a
                className="text-interaction-primary no-underline cursor-pointer"
                href={SUPPORT_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                contact support
              </a>{" "}
              .
            </Label>
          )}
        </div>
      ) : step === DialogStep.LoginTimeout ? ( // NOSONAR
        <div className="flex flex-col items-start gap-2 py-8 p-2">
          <Label size="large" displayType="loud" colorType="primary">
            Login is taking longer than expected.
          </Label>
          <Label size="medium" colorType="primary">
            The login process is taking longer than usual. Please try again later. If the issue continues,{" "}
            <a
              className="text-interaction-primary no-underline cursor-pointer"
              href={SUPPORT_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              contact support
            </a>
            .
          </Label>
        </div>
      ) : step === DialogStep.MFA ? ( // NOSONAR
        (() => {
          // The login alert packs CR's instruction (prompt) and an optional
          // retry error ("Invalid MFA code…") — split them so the instruction
          // shows as the prompt and the error renders in red below the field.
          const { prompt: mfaPrompt, error: mfaAlertError } = splitMfaAlert(
            ehrLoginStatus?.getLoginStatus?.getLoginStatus?.alert ??
              initialEhrLoginStatus?.getLoginStatus?.getLoginStatus?.alert,
          );
          const mfaInlineError = mfaAlertError ?? errorMessage;
          return (
            <div className="flex flex-col items-start gap-2 py-8 p-2">
              <div className="flex flex-col gap-4 w-full">
                <Paragraph colorType="primary">
                  {mfaPrompt ?? "Please enter your MFA code to continue."}
                </Paragraph>
                <RegisteredFormInput
                  formKey="mfa"
                  inputSize="full"
                  formState={formState}
                  label="MFA code"
                  onKeyDown={(e) => {
                    // Submit just the code on Enter, not the full form (which
                    // would fail username/password validation on the auto-open path).
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitMfaCode();
                    }
                  }}
                />
                {mfaInlineError && (
                  <div className="text-red-500 pb-3 flex gap-1">
                    <ExclamationTriangleIcon className="text-red-500 w-5 h-5" />
                    {mfaInlineError}
                  </div>
                )}
              </div>
            </div>
          );
        })()
      ) : step === DialogStep.NewLoginLoading && loginWithSavedCredentials ? ( // NOSONAR
        <div className="flex flex-col items-center gap-4 py-8 p-2">
          <Headline size="medium" colorType="primary">
            Logging in to {externalOrganizationName}
          </Headline>
          <Paragraph colorType="primary">Using your saved credentials. Please wait…</Paragraph>
        </div>
      ) : step === DialogStep.LoginForm || step === DialogStep.NewLoginLoading ? ( // NOSONAR
        <div className="flex flex-col p-2 gap-4">
          <div className="flex flex-col gap-4">
            <Headline size="medium" colorType="primary">
              Log in to {externalOrganizationName}
            </Headline>
            <Paragraph colorType="primary">
              Sign in with your <span className="font-extrabold">{externalOrganizationName}</span> credentials to
              securely access your client list through Frontera.
            </Paragraph>
          </div>
          <div className="flex flex-col">
            {/* Honeypot inputs absorb password-manager autofill before reaching the real EHR fields */}
            <input type="text" aria-hidden="true" tabIndex={-1} style={{ display: "none" }} autoComplete="username" />
            <input
              type="password"
              aria-hidden="true"
              tabIndex={-1}
              style={{ display: "none" }}
              autoComplete="current-password"
            />
            <RegisteredFormInput
              formKey="username"
              inputSize="full"
              formState={formState}
              label={isRethinkUser || isEnsoraUser ? "Username" : "Email"}
              disabled={isLoginInFlight}
              autoComplete="off"
              readOnly={usernameReadOnly}
              onFocus={() => setUsernameReadOnly(false)}
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              inputClassName={clsx("read-only:bg-white", errorInputClassName)}
            />
            <RegisteredFormInput
              formKey="password"
              inputSize="full"
              formState={formState}
              label="Password"
              type="password"
              disabled={isLoginInFlight}
              autoComplete="new-password"
              readOnly={passwordReadOnly}
              onFocus={() => setPasswordReadOnly(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && step === DialogStep.LoginForm) {
                  e.preventDefault();
                  onSubmit(e);
                }
              }}
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              inputClassName={clsx("read-only:bg-white", errorInputClassName)}
            />
            {errorMessage && (
              <div className="text-red-500 pb-3 flex gap-1">
                <ExclamationTriangleIcon className="text-red-500 w-5 h-5" />
                {errorMessage}
              </div>
            )}
            <Paragraph colorType="secondary">
              By providing your credentials, you let Frontera access your client data and agree Frontera or your EHR may
              text you a passcode per our or their terms of service.
            </Paragraph>
          </div>
        </div>
      ) : null}
    </>
  );

  // ─── Intro flow render (custom backdrop) ──────────────────

  if (showIntroFlow) {
    const showPanel = open;

    return (
      <FormContainer onSubmit={onSubmit} autoComplete="off">
        <Transition.Root show={showPanel} as={Fragment}>
          <HeadlessUIDialog className="relative z-50" onClose={() => {}}>
            {/* Backdrop — transitions from semi-transparent gray to solid limestone */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 transition-colors duration-500 ease-in-out"
                style={{
                  backgroundColor: isLimestoneBackdrop ? LIMESTONE_50 : "rgba(107, 114, 128, 0.75)",
                }}
              />
            </Transition.Child>

            {/* Panel */}
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <HeadlessUIDialog.Panel
                    className={
                      isReachingOutCard
                        ? "relative flex transform flex-col justify-center items-center rounded-2xl bg-white transition-all w-[453px] h-[400px] py-6"
                        : step === DialogStep.Consent
                          ? "relative flex transform flex-col rounded-2xl bg-white text-left border border-limestone-200 transition-all pb-12 sm:my-8 sm:w-full sm:max-w-xl"
                          : "relative flex transform flex-col rounded-2xl bg-white text-left border border-limestone-200 transition-all sm:my-8 sm:w-full sm:max-w-xl"
                    }
                    style={isReachingOutCard ? { boxShadow: "0 4px 25px 0 rgba(0, 0, 0, 0.12)" } : undefined}
                  >
                    {/* Reaching Out + saved-credential auto-login — standalone centered card */}
                    {isReachingOutCard && (
                      <StepTransition stepKey={isSavedCredentialLoading ? "logging-in" : "reaching-out"}>
                        <div className="flex flex-col items-center gap-px">
                          <img
                            className="w-[100px] h-[100px]"
                            src={organizationLogo}
                            alt={`${externalOrganizationName} logo`}
                          />
                          <Paragraph colorType="primary" displayType="loud" className="mt-6 text-center">
                            {isSavedCredentialLoading
                              ? `Logging in to ${externalOrganizationName}`
                              : `Reaching out to ${externalOrganizationName}`}
                          </Paragraph>
                          <AnimatedDots />
                        </div>
                      </StepTransition>
                    )}

                    {/* Other steps: standard dialog layout */}
                    {!isReachingOutCard && (
                      <>
                        {/* Header: X close for Consent, empty spacer for other steps */}
                        <div className="px-6 pt-6">
                          <div className="mb-4 flex w-full items-center justify-end">
                            {step === DialogStep.Consent && (
                              <XMarkIcon
                                className="h-6 w-6 cursor-pointer text-text-secondary hover:text-text-primary"
                                onClick={() => closeDialog()}
                              />
                            )}
                          </div>
                        </div>
                        <div className="px-6 min-h-[200px]">
                          {/* Step 2: Consent */}
                          {step === DialogStep.Consent && (
                            <StepTransition stepKey="consent">
                              <div className="flex items-center justify-center gap-4 mb-6">
                                <div className="w-[64px] h-[64px] rounded-full border border-limestone-400 flex items-center justify-center shrink-0">
                                  <img className="w-[45px] h-[45px]" src={fronteraIcon} alt="Frontera" />
                                </div>
                                <ArrowsRightLeftIcon className="h-5 w-5 text-limestone-400 shrink-0" />
                                <img
                                  className="w-[64px] h-[64px] shrink-0"
                                  src={organizationLogo}
                                  alt={`${externalOrganizationName} emblem`}
                                />
                              </div>
                              <div className="flex flex-col gap-4 px-2">
                                <Headline size="medium" colorType="primary" className="text-center">
                                  Frontera works with {externalOrganizationName}
                                  <br />
                                  to access your most up to date data
                                </Headline>
                                <Paragraph colorType="secondary" className="text-center">
                                  By logging into your {externalOrganizationName}, we securely access your client list
                                  and information to make your experience within Frontera more seamless.
                                </Paragraph>
                                <div className="flex flex-col gap-3 mt-2 p-3 bg-limestone-50 rounded-lg">
                                  <div className="flex items-start gap-2.5">
                                    <ShieldCheckIcon className="h-5 w-5 text-limestone-600 shrink-0 mt-0.5" />
                                    <Paragraph size="small" colorType="secondary">
                                      Frontera will access only the data requested. We never share your login details
                                      with anyone
                                    </Paragraph>
                                  </div>
                                  <div className="flex items-start gap-2.5">
                                    <LockClosedIcon className="h-5 w-5 text-limestone-600 shrink-0 mt-0.5" />
                                    <Paragraph size="small" colorType="secondary">
                                      Your data is encrypted for your protection
                                    </Paragraph>
                                  </div>
                                  <div className="flex items-start gap-2.5">
                                    <ArrowPathIcon className="h-5 w-5 text-limestone-600 shrink-0 mt-0.5" />
                                    <Paragraph size="small" colorType="secondary">
                                      You can disconnect your account at any time
                                    </Paragraph>
                                  </div>
                                </div>
                                <Button
                                  appearance="primary"
                                  text="Agree"
                                  className="w-full"
                                  onClick={(e) => {
                                    e?.preventDefault();
                                    setStep(DialogStep.CheckExistingLoginLoading);
                                  }}
                                />
                                <Paragraph size="extra-small" colorType="tertiary" className="text-center">
                                  By clicking Agree, you accept Frontera's{" "}
                                  <a
                                    href="https://fronterahealth.com/terms-of-use/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-interaction-primary no-underline cursor-pointer"
                                  >
                                    Terms of Service
                                  </a>{" "}
                                  and{" "}
                                  <a
                                    href="https://fronterahealth.com/privacy-policy/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-interaction-primary no-underline cursor-pointer"
                                  >
                                    Privacy Policy
                                  </a>
                                  .
                                </Paragraph>
                              </div>
                            </StepTransition>
                          )}

                          {/* Steps 3+: Login flow */}
                          {!isIntroStep && (
                            <StepTransition stepKey={`login-${step}`}>{renderLoginStepContent()}</StepTransition>
                          )}
                        </div>

                        {/* Footer buttons — only for login steps, not Consent */}
                        {step !== DialogStep.Consent && (
                          <div className="flex w-full items-center justify-between p-6 pb-6 pt-8 sm:px-6">
                            <div className="ml-auto flex items-center gap-x-4">
                              <Button
                                appearance="secondary"
                                text="Cancel"
                                onClick={(e) => {
                                  e?.preventDefault();
                                  closeDialog();
                                }}
                              />
                              <Button
                                appearance="primary"
                                text={
                                  <div className="flex gap-1 w-max">
                                    {isLoginInFlight ? (
                                      <ArrowPathIcon className="h-6 w-6 animate-spin p-1 text-white" />
                                    ) : null}
                                    {step === DialogStep.MFA
                                      ? "Continue"
                                      : step === DialogStep.LoginTimeout
                                        ? "Try Again"
                                        : step === DialogStep.NewLoginLoading && loginWithSavedCredentials
                                          ? "Logging in…"
                                          : "Log In"}
                                  </div>
                                }
                                onClick={(e) => {
                                  e?.preventDefault();
                                  if (step === DialogStep.MFA) {
                                    submitMfaCode();
                                  } else if (step === DialogStep.LoginForm) {
                                    onSubmit(e);
                                  } else {
                                    setErrorMessage(null);
                                    setCredentialError(false);
                                    if (loginWithSavedCredentials) {
                                      startLoginWithSavedCredentials();
                                    } else {
                                      setStep(DialogStep.LoginForm);
                                    }
                                  }
                                }}
                                disabled={
                                  step === DialogStep.CheckExistingLoginLoading ||
                                  step === DialogStep.NewLoginLoading ||
                                  isLoginInFlight ||
                                  loginAttemptsExhausted
                                }
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </HeadlessUIDialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </HeadlessUIDialog>
        </Transition.Root>
      </FormContainer>
    );
  }

  // ─── Standard flow render (existing Dialog component) ─────

  return (
    <FormContainer onSubmit={onSubmit} autoComplete="off">
      <Dialog
        title=""
        open={open && step !== DialogStep.CheckExistingLoginLoading}
        setOpen={() => {}}
        primaryButton={{
          text: (
            <div className="flex gap-1 w-max">
              {isLoginInFlight ? <ArrowPathIcon className="h-6 w-6 animate-spin p-1 text-white" /> : null}
              {step === DialogStep.MFA
                ? "Continue"
                : step === DialogStep.LoginTimeout
                  ? "Try Again"
                  : step === DialogStep.NewLoginLoading && loginWithSavedCredentials
                    ? "Logging in…"
                    : "Log In"}
            </div>
          ),
          onClick: (e) => {
            e?.preventDefault();
            if (step === DialogStep.MFA) {
              submitMfaCode();
            } else if (step === DialogStep.LoginForm) {
              onSubmit(e);
            } else {
              setErrorMessage(null);
              setCredentialError(false);
              if (loginWithSavedCredentials) {
                startLoginWithSavedCredentials();
              } else {
                setStep(DialogStep.LoginForm);
              }
            }
          },
          disabled:
            step == DialogStep.CheckExistingLoginLoading ||
            step == DialogStep.NewLoginLoading ||
            isLoginInFlight ||
            loginAttemptsExhausted,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: (e) => {
            e?.preventDefault();
            closeDialog();
          },
        }}
        size={"xl"}
        bodyClassName="min-h-[200px]"
        showCloseButton={false}
      >
        {renderLoginStepContent()}
      </Dialog>
    </FormContainer>
  );
};
